import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import styles from './index.module.scss'
import { forwardRef } from "react"
import * as _ from "underscore";
import LabelBox from "../LabelBox"
import ImageRender from "../ImageRender"
import LabelToolBox from "../LabelToolBox"
import VideoController from "../VideoController"
import VideoLabels from "../VideoLabels"
import { useDrag, useDrop, useDragLayer } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import Utils from '@utils/utils'
import { message, Popover } from "antd";
import { VideoContextProvider } from "../../hooks/VideoContext";

const ImageRenderContainer = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => ({
        changeMode: setMode,
        undo: handleUndo,
        reset: handleReset,
    }))

    const [transform, setTransform] = useState()
    const [imgSize, setImgSize] = useState({ width: 0, height: 0 })

    const handleLoad = (e) => {
        setImgSize({
            width: e.clientWidth,
            height: e.clientHeight
        })
    }

    const style = useMemo(() => {
        if (!transform) return
        return {
            display: imgSize.width == 0 || imgSize.height == 0 ? "none" : "block",
            width: imgSize.width,
            height: imgSize.height,
            transform: `translate3d(${transform.x}px, ${transform.y}px, 0px) scale3d(${transform.scale}, ${transform.scale}, 1) rotate(0deg)`
        }
    }, [transform, imgSize])

    const imageRenderRef = useRef()
    const onWheel = e => imageRenderRef.current.onWheel(e)
    const onMouseMove = e => imageRenderRef.current.onMouseMove(e)
    const onMouseLeave = e => imageRenderRef.current.onMouseUp(e)

    // 标注工具
    // rect/ellipse/brush/arrow/text
    const [mode, setMode] = useState()

    const imageReady = useMemo(() => {
        return imgSize.width * imgSize.height > 0
    }, [imgSize])

    const handleCopy = (text) => {
        Utils.copyText(text)
        message.success(`"${text}" 已复制`)
    }

    const labelBoxRef = useRef()
    const handleUndo = () => labelBoxRef.current && labelBoxRef.current.handleUndo()
    const handleReset = () => labelBoxRef.current && labelBoxRef.current.handleReset()

    return (
        <div
            className={styles.ImageRenderContainer}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
        >
            <ImageRender
                ref={imageRenderRef}
                src={props.src}
                onTransform={setTransform}
                onLoad={handleLoad}
            />
            {imageReady ?
            <>
            <LabelBox
                ref={labelBoxRef}
                style={style}
                transform={transform}
                width={imgSize.width}
                height={imgSize.height}
                labels={props.labels}
                readonly={props.readonly}
                mode={mode}
                onWheel={onWheel}
                onLabelChange={props.onLabel}
            />
            {
                props.name &&
                <div className={styles.ImageName}>
                    {props.name}
                </div>
            } {
                props.extraName &&
                <div className={styles.ImageExtraName} onClick={() => handleCopy(props.extraName)}>
                    {props.extraName}
                </div>
            }
            {props.children}
            </> : null}
        </div>
    )
})

const CustomDragLayer = (props) => {
    //monitor 是drag的 monitor
    const { itemType, isDragging, item, initialOffset, currentOffset } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        itemType: monitor.getItemType(),
        initialOffset: monitor.getInitialSourceClientOffset(),
        currentOffset: monitor.getSourceClientOffset(),
        isDragging: monitor.isDragging(),
    }));

    if (!isDragging || !props.src) {
        return null;
    }

    const getItemStyles = () => {
        let { x, y } = currentOffset
        const transform = `translate(${x-40}px, ${y-40}px)`;
        return {
            position: 'fixed',
            pointerEvents: 'none',
            zIndex: 100,
            left: 0,
            top: 0,
            width: 80,
            transform
        }
    }
    return (
        <div style={getItemStyles()}>
            <img src={props.src} width={200} />
        </div>
    )
}

const ImageRenderBox = (props) => {
    const dropRef = useRef()
    const { item, index, canDrag, canSwapDrop, canReplaceDrop } = props
    const [{ isDragging }, drag, preview] = useDrag({
        type: 'DragDropSwapBox',
        item: { index },
        end: (item, monitor) => {},
        isDragging: (monitor) => {
            return index === monitor.getItem().index;
        },
        canDrag: () => canDrag,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    })
    const [, drop] = useDrop({
        accept: 'DragDropSwapBox',
        hover: (item, monitor) => {},
        canDrop: () => canSwapDrop,
        drop: (item, monitor) => {
            if (!monitor.canDrop()) return
            let dragIndex = item.index;
            let dropIndex = index;
            if (dragIndex === dropIndex) return; // 如果回到自己的坑，那就什么都不做
            props.onSwap(dragIndex, dropIndex)
        },
    })
    const [, drop2] = useDrop({
        accept: 'DragDropReplaceBox',
        hover: (item, monitor) => {},
        canDrop: () => canReplaceDrop,
        drop: (item, monitor) => {
            if (!monitor.canDrop()) return
            let dragIndex = item.index;
            let dropIndex = index;
            props.onReplace(dragIndex, dropIndex)
        },
    })
    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true }); // 隐藏拖拽dom
    }, []);

    const handleDownload = () => {
        if (!item) return
        const url = item.url
        const name = item.name
        const extension = url.split('?').shift().split('.').pop()
        Utils.downloadUrl(item.url, name ? `${name}.${extension}` : null)
        message.success("图片已下载")
    }

    const imageRenderRef = useRef()

    const isVideo = useMemo(() => {
        return /\.mp4/.test(item?.url)
    }, [item])

    useEffect(() => {
        console.log('item.annotations', item?.annotations)
    }, [item])

    return (
        <div
            className={styles.ImageRenderBox}
            style={{ borderColor: isDragging ? "#1677ff" : "#999" }}
            ref={drop2(drop(dropRef))}
        >
            <div className={styles.ImageRenderWrap}>
            {
                item ?
                <ImageRenderContainer
                    ref={imageRenderRef}
                    key={item.name}
                    src={item.url}
                    name={item.name}
                    extraName={item.extraName}
                    labels={!isVideo ? item.annotations : []}
                    readonly={item.readonly}
                    onLabel={labels => props.onLabel(labels, item.imageId)}
                >
                {
                    item?.extraRender?.()
                }
                </ImageRenderContainer> : null
            }
            </div>
            <div className={styles.LabelToolWrap}>
            {
                (item && !isVideo && !item.readonly) ?
                <LabelToolBox
                    onChange={value => imageRenderRef.current.changeMode(value)}
                    onUndo={() => imageRenderRef.current.undo()}
                    onReset={() => imageRenderRef.current.reset()}
                /> : null
            }{
                (item && isVideo) ?
                <div className={styles.VideoControlWrap}>
                    <VideoController />
                </div> : null
            }
            </div>
            <div className={styles.RightBottomTools}>
            {
                props.downloadable ?
                <div className={`${styles.DownloadTrigger}`} onClick={handleDownload}></div> :
                null
            } {
                props.fixedable ? // 如果可手动固定，需要支持手动固定
                <div className={`${styles.FixedTrigger} ${item?.fixed && styles.isFixed}`} onClick={props.onFixed}></div> :
                null
            } {
                canDrag ?
                <div ref={drag} className={`${styles.DragTrigger} ${isDragging && styles.isDragging}`}></div> :
                null
            } {
                isVideo ?
                <Popover
                    placement="topRight"
                    arrow={false}
                    getPopupContainer={node => node}
                    content={
                        <VideoLabels
                            src={item.url}
                            readonly={item.readonly}
                            labels={item.annotations}
                            onLabel={labels => props.onLabel(labels, item.imageId)}
                        />
                    }
                >
                    <div className={styles.VideoLabelTrigger}></div>
                </Popover> : null
            }
            </div>
            {
                isDragging &&
                <CustomDragLayer src={Utils.getImgThumbnail(item?.url)} />
            }
        </div>
    )
}

export default (props) => (
    <VideoContextProvider>
        <ImageRenderBox {...props} />
    </VideoContextProvider>
)